import { Paperclip, ReceiptText } from "lucide-react";
import { useMemo } from "react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { WALLET_DEBITS_PAGE_SIZE } from "../constants/walletDebits.constants";
import type { WalletDebit } from "../types/walletDebits.types";
import { WalletDebitStatusPill } from "./WalletDebitStatusPill";

export interface WalletDebitsListProps {
  rows: WalletDebit[];
  isLoading: boolean;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Opens the debit's detail, where it is sent, cancelled or corrected. */
  onOpen: (debit: WalletDebit) => void;
}

/** The history twice, picked by CSS: a table from `xl` up, cards below it (design-system rule 7). */
export function WalletDebitsList(props: WalletDebitsListProps) {
  return (
    <>
      <div className="hidden xl:block">
        <WalletDebitsTable {...props} />
      </div>
      <div className="xl:hidden">
        <WalletDebitsCards {...props} />
      </div>
    </>
  );
}

function Amount({ debit }: { debit: WalletDebit }) {
  return (
    <ColocaAmountDisplay
      value={`-${debit.amount}`}
      currency={debit.currency}
      fractionDigits={minorUnitsFor(debit.currency)}
      size="sm"
      signed
    />
  );
}

function AttachmentsCount({ debit, className }: { debit: WalletDebit; className?: string }) {
  const { t } = useTranslations("walletDebits");
  const count = debit.attachments.length;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs",
        count ? "text-foreground" : "text-muted-foreground",
        className,
      )}
    >
      <Paperclip className="size-3.5 shrink-0" aria-hidden="true" />
      {count === 0 ? t("attachments_count_none") : t("attachments_count", { count })}
    </span>
  );
}

function EmptyHistory() {
  const { t } = useTranslations("walletDebits");
  return (
    <ColocaEmpty
      icon={ReceiptText}
      title={t("empty_title")}
      description={t("empty_description")}
      surface="card"
    />
  );
}

function WalletDebitsTable({ rows, isLoading, page, total, onPageChange, onOpen }: WalletDebitsListProps) {
  const { t, activeLanguage } = useTranslations("walletDebits");
  const columns = useMemo<ColocaDataTableColumn<WalletDebit>[]>(
    () => [
      {
        id: "date",
        header: t("col_date"),
        cell: (row) => (
          <span className="font-mono text-xs text-muted-foreground tabular-nums">
            {formatDateTime(row.created_at, { locale: activeLanguage })}
          </span>
        ),
      },
      {
        id: "status",
        header: t("col_status"),
        cell: (row) => <WalletDebitStatusPill debit={row} />,
      },
      {
        id: "organization",
        header: t("col_organization"),
        cellClassName: "max-w-56",
        cell: (row) => (
          <span className="block truncate font-medium text-foreground" title={row.organization_name ?? ""}>
            {row.organization_name ?? "—"}
          </span>
        ),
      },
      {
        id: "amount",
        header: t("col_amount"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (row) => <Amount debit={row} />,
      },
      {
        id: "external_reference",
        header: t("col_external_reference"),
        cellClassName: "max-w-48",
        cell: (row) => (
          <span
            className="block truncate font-mono text-xs text-foreground"
            title={row.external_reference ?? ""}
          >
            {row.external_reference ?? "—"}
          </span>
        ),
      },
      {
        id: "concept",
        header: t("col_concept"),
        cellClassName: "max-w-72",
        cell: (row) => (
          <div className="min-w-0">
            <span className="block truncate text-sm text-foreground" title={row.concept}>
              {row.concept}
            </span>
            {row.internal_note && (
              <span className="block truncate text-xs text-muted-foreground" title={row.internal_note}>
                {row.internal_note}
              </span>
            )}
          </div>
        ),
      },
      {
        id: "reference",
        header: t("col_reference"),
        cell: (row) => <span className="font-mono text-xs text-foreground">{row.reference}</span>,
      },
      {
        id: "by",
        header: t("col_by"),
        cellClassName: "max-w-52",
        cell: (row) => (
          <span className="block truncate text-xs text-muted-foreground" title={row.created_by_email ?? ""}>
            {row.created_by_email ?? "—"}
          </span>
        ),
      },
      {
        id: "attachments",
        header: t("col_attachments"),
        cell: (row) => <AttachmentsCount debit={row} />,
      },
    ],
    [activeLanguage, t],
  );

  return (
    <ColocaDataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      onRowClick={onOpen}
      loading={isLoading}
      skeletonRows={6}
      minWidth={1380}
      empty={<EmptyHistory />}
      footer={
        total > WALLET_DEBITS_PAGE_SIZE ? (
          <ColocaPagination
            page={page}
            perPage={WALLET_DEBITS_PAGE_SIZE}
            total={total}
            withSeparator={false}
            size="sm"
            onPageChange={onPageChange}
          />
        ) : undefined
      }
    />
  );
}

function WalletDebitsCards({ rows, isLoading, page, total, onPageChange, onOpen }: WalletDebitsListProps) {
  const { t, activeLanguage } = useTranslations("walletDebits");
  if (isLoading && rows.length === 0) {
    return (
      <div className="space-y-3" aria-busy="true">
        <ColocaSkeleton className="h-32 w-full rounded-xl" />
        <ColocaSkeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }
  if (rows.length === 0) return <EmptyHistory />;
  return (
    <div className="space-y-3">
      <ul className="space-y-3">
        {rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => onOpen(row)}
              className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-muted/40 focus-visible:outline-2 focus-visible:outline-ring"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {row.organization_name ?? "—"}
                  </p>
                  <p className="font-mono text-[11px] text-muted-foreground">
                    {row.reference} · {formatDateTime(row.created_at, { locale: activeLanguage })}
                  </p>
                </div>
                <WalletDebitStatusPill debit={row} />
              </div>
              <div className="mt-2 flex items-center justify-between gap-3 text-foreground">
                <Amount debit={row} />
                <AttachmentsCount debit={row} className="shrink-0" />
              </div>
              {row.external_reference && (
                <p className="mt-2 truncate font-mono text-xs text-foreground">
                  {t("card_external_reference", { reference: row.external_reference })}
                </p>
              )}
              <p className="mt-1 text-sm break-words text-foreground">{row.concept}</p>
              {row.internal_note && (
                <p className="mt-1 text-xs break-words text-muted-foreground">
                  {t("card_note", { note: row.internal_note })}
                </p>
              )}
              {row.created_by_email && (
                <p className="mt-1 truncate text-xs text-muted-foreground">
                  {t("card_by", { email: row.created_by_email })}
                </p>
              )}
            </button>
          </li>
        ))}
      </ul>
      {total > WALLET_DEBITS_PAGE_SIZE && (
        <ColocaPagination
          page={page}
          perPage={WALLET_DEBITS_PAGE_SIZE}
          total={total}
          size="sm"
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
